<template>
  <div class="absolute w-full h-full" style="bottom:0;">
    <LayoutPerson
      v-if="councillor"
      :color="partyColor"
      v-statusbar:[partyColor]
      @share="share"
    >
      <template v-slot:header>
        <!-- Portrait -->
        <candidate-profile-top class="relative pb-6 lg:pb-0">
          <div class="profile-picture pointer-events-none">
            <img
              :src="
                councillor.urlImage ||
                  require(`@/assets/images/genicon-candidate-unknown.svg`)
              "
              draggable="false"
              alt=""
              v-fallback="
                require(`@/assets/images/genicon-candidate-unknown.svg`)
              "
            />
          </div>
        </candidate-profile-top>
      </template>
      <!-- Content -->
      <template v-slot:information>
        <h1
          class="text-chplus-title-l font-bold mx-auto mt-2 mb-2"
          style="text-overflow: ellipsis;max-width: 95%;"
          :style="{ color: partyColor }"
        >
          {{ councillor.firstName }} {{ councillor.lastName.toUpperCase() }}
        </h1>
        <div class="match-progress"></div>
        <p class="text-chplus-l text-interaction-dark mt-4">
          {{ councillor.roleTitle }}
        </p>
        <p
          v-if="councillor.additionalRoleTitle"
          class="text-chplus-regular text-interaction-dark mb-0"
        >
          {{ councillor.additionalRoleTitle }}
        </p>
        <div class="flex justify-center text-white my-5">
          <button
            class="w-fit rounded-lg p-2 pl-4 pr-4 pointer-events-none"
            :style="{
              background:
                councillor.parliamentaryGroup &&
                councillor.parliamentaryGroup.color
                  ? councillor.parliamentaryGroup.color
                  : 'var(--interaction-dark)'
            }"
          >
            {{ councillor.parliamentaryGroup.name }}
          </button>
        </div>
        <candidate-info-box :candidate="councillor" />
      </template>
      <template v-slot:tabs>
        <!-- Tabs -->
        <div ref="tabTop">
          <content-tabs>
            <content-tab
              :currentTab="activeTab"
              :color="partyColor"
              tabName="profile"
              @setTab="setTab"
            >
              <icon-parliament />
            </content-tab>
            <content-tab
              :currentTab="activeTab"
              :color="partyColor"
              tabName="engagement"
              @setTab="setTab"
            >
              <icon-people-group />
            </content-tab>
            <content-tab
              :currentTab="activeTab"
              :color="partyColor"
              tabName="votes"
              :unavailable="!isOnline || !areVotesAvailable"
              @setTab="setTab"
            >
              <icon-votes />
            </content-tab>
            <content-tab
              :currentTab="activeTab"
              :color="partyColor"
              tabName="news"
              :unavailable="!isOnline || !areNewsAvailable"
              @setTab="setTab"
            >
              <icon-news />
            </content-tab>
            <content-tab
              v-if="isLoggedIn"
              :currentTab="activeTab"
              :color="partyColor"
              tabName="notes"
              @setTab="setTab"
            >
              <icon-pin />
            </content-tab>
          </content-tabs>
        </div>
      </template>
      <template v-slot:content>
        <!-- Tab Bodys -->
        <!-- profile tab -->
        <tab
          :activeTab="activeTab"
          :color="partyColor"
          tabName="profile"
          :title="$t('views.single.councillor.parliamentary_groups_title')"
        >
          <div class="mb-12 flex flex-col gap-3">
            <person-info-slice
              v-for="{ id, name, memberships } in councilMemberships"
              :key="id"
              :color="partyColor"
              :title="name"
              :showPinned="showPins"
              :pinnedColor="
                notesByTypeAndReference.councillor_profile &&
                  notesByTypeAndReference.councillor_profile[`membership/${id}`]
                    ?.color
              "
              @pinned="onPin('councillor_profile', `membership/${id}`)"
            >
              <ul class="list-disc text-left">
                <li v-for="membership in memberships" :key="membership.id">
                  {{ membership.committeeName }}
                </li>
              </ul>
            </person-info-slice>
          </div>
        </tab>
        <!-- engagement tab -->
        <tab
          :activeTab="activeTab"
          :color="partyColor"
          tabName="engagement"
          :title="$t('views.single.councillor.commitments_title')"
        >
          <div class="flex flex-col gap-3 mb-12">
            <person-info-slice
              v-if="councillorMandates.length > 0"
              :color="partyColor"
              :title="$t('views.single.councillor.mandates_subtitle')"
              :showPinned="showPins"
              :pinnedColor="
                notesByTypeAndReference.councillor_engagement?.mandates?.color
              "
              @pinned="onPin('councillor_engagement', 'mandates')"
            >
              <ul class="list-disc text-left">
                <li
                  v-for="(councillorMandate, i) in councillorMandates"
                  :key="i"
                  class="mb-2"
                >
                  {{ councillorMandate }}
                </li>
              </ul>
            </person-info-slice>
            <person-info-slice
              v-if="councillor.concerns"
              :color="partyColor"
              :showPinned="showPins"
              :pinnedColor="
                notesByTypeAndReference.councillor_engagement?.concerns?.color
              "
              :title="$t('components.people.profile.concerns_title')"
              @pinned="onPin('councillor_engagement', 'concerns')"
            >
              <concerns :concerns="councillor.concerns" />
            </person-info-slice>
          </div>
        </tab>
        <!-- votes tab -->
        <tab
          :activeTab="activeTab"
          :color="partyColor"
          v-if="activeTab === 'votes'"
          tabName="votes"
          :title="$t('views.government.vote.title')"
        >
          <vote-councillor-tab
            :partyColor="partyColor"
            :candidateId="parseInt(id)"
            @pinned="id => onPin('councillor_vote_pin', String(id))"
            :pinnedVotes="notesByTypeAndReference.councillor_vote_pin"
            v-if="isOnline"
          />
          <div
            v-else
            class="text-color-candidate mb-8 h-6 text-chplus-candidate-info"
          >
            {{ $t("components.candidate.candidate_card.news_no_internet") }}
          </div>
        </tab>
        <tab
          :activeTab="activeTab"
          :color="partyColor"
          tabName="news"
          :title="$t('views.single.candidate.news.title')"
        >
          <councillor-tab-news
            :partyColor="partyColor"
            :councillor="councillor"
            :notepad="notepad"
            @pinned="onPin"
            v-if="isOnline && activeTab === 'news'"
          />
          <div
            v-else-if="!isOnline"
            class="text-color-candidate mb-8 h-6 text-chplus-candidate-info"
          >
            {{ $t("components.candidate.candidate_card.news_no_internet") }}
          </div>
        </tab>
        <tab
          :activeTab="activeTab"
          :color="partyColor"
          tabName="notes"
          :title="$t('views.single.candidate.notes.title')"
        >
          <councillor-tab-notes
            :notes="notepad"
            :partyColor="partyColor"
            :councillor="councillor"
            :councilMemberships="councilMemberships"
            @pinned="onPin"
            v-if="isOnline && activeTab === 'notes'"
          />
          <div
            v-else-if="!isOnline"
            class="text-color-candidate mb-8 h-6 text-chplus-candidate-info"
          >
            {{ $t("components.candidate.candidate_card.news_no_internet") }}
          </div>
        </tab>
        <span class="text-chplus-xs">
          {{
            $t("misc.download_source", {
              source: "ws-old.parlament.ch",
              date: new Date(councillor.updatedAt).toLocaleDateString()
            })
          }}
        </span>
        <div class="flex justify-center mt-10 mb-3">
          <a
            :href="councillorLink"
            target="_blank"
            class="w-fit rounded-lg p-3 bg-interaction-dark text-white"
          >
            {{ $t("views.single.councillor.link_profile") }}
            <icon-link-profile />
          </a>
        </div>
      </template>
      </LayoutPerson>
    <select-pin-button />
  </div>
</template>

<script>
// Utilities
import { mapGetters } from "vuex";
import { LanguageCode } from "../../utils/LanguageCode";
import client from "api-client";
import FallbackImg from "@/directives/FallbackImg";
import { ViewStateMixin } from "../../utils/ViewStateMixin";
import OnlineMixin from "../../utils/OnlineMixin";
import { groupBy } from "@/utils/ArrayUtility";

// Components
import CandidateProfileTop from "@/components/layouts/CandidateProfileTop";
import CandidateInfoBox from "@/components/people/CandidateInfoBox";
import ContentTabs from "@/components/nav/ContentTabs";
import ContentTab from "@/components/nav/ContentTab";
import Tab from "@/components/people/Tab";
import VoteCouncillorTab from "@/components/vote/VoteCouncillorTab";
import CouncillorTabNotes from "@/components/people/CouncillorTabNotes";
import SelectPinButton from "@/components/buttons/SelectPinButton";
import PersonInfoSlice from "@/components/people/PersonInfoSlice";
import Concerns from "../../components/people/profile/Concerns.vue";

// Icons
import IconLinkProfile from "../../components/icons/IconLinkProfile";
import IconParliament from "../../components/icons/IconParliament.vue";
import IconPeopleGroup from "../../components/icons/IconPeopleGroup.vue";
import IconVotes from "@/components/icons/IconVotes.vue";
import IconNews from "@/components/icons/IconNews";
import CouncillorTabNews from "../../components/people/CouncillorTabNews.vue";
import IconPin from "@/components/icons/IconPin.vue";
import ShareLinkMixin from "../../utils/ShareLinkMixin";
import IsMobileMixin from "../../utils/IsMobileMixin";
import LayoutPerson from "../../components/layouts/LayoutPerson.vue";
import StatusBarDirective from "@/directives/StatusBarDirective";

export default {
  name: "Councillor",
  directives: {
    fallback: FallbackImg,
    statusbar: StatusBarDirective
  },
  components: {
    CandidateInfoBox,
    CandidateProfileTop,
    LayoutPerson,
    ContentTabs,
    ContentTab,
    Tab,
    VoteCouncillorTab,
    CouncillorTabNotes,
    PersonInfoSlice,
    Concerns,
    // Icons
    IconLinkProfile,
    IconParliament,
    IconPeopleGroup,
    IconVotes,
    IconNews,
    CouncillorTabNews,
    SelectPinButton,
    IconPin
  },
  props: ["id"],
  mixins: [ViewStateMixin, OnlineMixin, ShareLinkMixin, IsMobileMixin],
  data: () => ({
    councillor: null,
    areVotesAvailable: false,
    areNewsAvailable: false,
    councils: [],
    savedQuery: {},
    notepad: []
  }),
  async created() {
    this.$store.dispatch("news/fetchMediaOutlets");
    const [councillor, councils, notepad] = await Promise.all([
      client.fetchCouncillor(
        Number(this.id),
        LanguageCode.get(this.contentLanguage)
      ),
      client.fetchCouncils(LanguageCode.get(this.contentLanguage)),
      this.isLoggedIn
        ? client.fetchCouncillorNotepad(this.user.id, Number(this.id))
        : Promise.resolve([])
    ]);

    client
      .fetchCouncillorVotes(Number(this.id))
      .then(votes => votes.data.length > 0)
      .catch(() => false)
      .then(available => (this.areVotesAvailable = available));

    client
      .fetchCouncillorArticles(Number(this.id))
      .then(news => news.data.length > 0)
      .catch(() => false)
      .then(available => (this.areNewsAvailable = available));

    this.councillor = councillor;
    this.councils = councils;
    this.notepad = notepad;
  },
  computed: {
    ...mapGetters("user", ["contentLanguage", "user", "isLoggedIn"]),
    ...mapGetters("locale", ["locale"]),
    ...mapGetters("notes", ["showPins", "selectedColor"]),
    councilMemberships() {
      const validCommittees = (this.councillor.currentCommittees || []).filter(
        membership => !!membership.councilId
      );
      const membershipsPerCouncil = groupBy(
        validCommittees,
        membership => membership.councilId
      );
      return this.councils
        .filter(council => membershipsPerCouncil[council.id])
        .map(council => ({
          id: council.id,
          name: council.name,
          memberships: membershipsPerCouncil[council.id]
        }));
    },
    councillorMandates() {
      return [
        this.councillor?.mandate,
        this.councillor?.additionalMandate,
        this.councillor?.additionalActivity
      ].filter(Boolean);
    },
    activeTab() {
      return this.$query.tab || "profile";
    },
    partyColor() {
      return this.councillor && this.councillor.partyColor
        ? this.councillor.partyColor
        : "#808080";
    },
    councillorLink() {
      if (!this.councillor.firstName || !this.councillor.lastName) {
        return "";
      }
      const name = `${this.councillor.firstName.toLowerCase()}-${this.councillor.lastName.toLowerCase()}`.replace(
        /\s/g,
        "-"
      );
      return `https://www.parlament.ch/${this.locale}/biografie/${name}/${this.councillor.parliamentId}`;
    },
    notesByTypeAndReference() {
      const notesByType = groupBy(this.notepad, n => n.type);
      return Object.entries(notesByType).reduce((acc, [type, notes]) => {
        const notesByReference = notes.reduce(
          (acc, note) => Object.assign(acc, { [note.reference]: note }),
          {}
        );
        return Object.assign(acc, { [type]: notesByReference });
      }, {});
    }
  },
  methods: {
    async onPin(type, reference, color) {
      const entry = this.notesByTypeAndReference[type]?.[reference];
      if (entry) {
        // If the note is already pinned, delete it
        if (!entry.id) {
          // Happens when a newly created note is deleted before it's saved
          return;
        }
        this.notepad = this.notepad.filter(e => e !== entry);
        try {
          await client.deleteCouncillorNote(
            this.user.id,
            parseInt(this.councillor.id),
            entry.id
          );
        } catch (error) {
          this.notepad.push(entry);
        }
      } else {
        // If the note is not pinned, create it
        const newEntry = {
          id: null,
          type,
          reference,
          color: color || this.selectedColor
        };
        this.notepad.push(newEntry);
        try {
          const { id } = await client.createCouncillorNote(
            this.user.id,
            parseInt(this.councillor.id),
            newEntry.type,
            newEntry.reference,
            newEntry.color
          );
          newEntry.id = id;
        } catch (error) {
          this.notepad = this.notepad.filter(e => e !== newEntry);
        }
      }
    },
    setTab(tab) {
      this.savedQuery[this.activeTab] = { ...this.$query };
      const listElement = this.$refs.tabTop;
      listElement.scrollIntoView({ behavior: "auto" });
      const newQuery = {
        ...(this.savedQuery[tab] || {}),
        tab: tab
      };
      this.replaceQuery(newQuery);
    },
    share() {
      this.shareCurrentLink(
        this.$t("views.single.councillor.share_subject"),
        this.$t("views.single.councillor.share_message", {
          name: `${
            this.councillor.firstName
          } ${this.councillor.lastName.toUpperCase()}`
        })
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.profile-picture,
.smart-spider-box {
  width: 10.625rem;
  height: 10.625rem;
  flex: 0 0 10.625rem;
}

.profile-picture {
  position: relative;

  box-shadow: rgba(255, 238, 220, 0.2) 0 0 40px 10px;
  border-radius: 50%;

  img {
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    margin: 0 auto;
  }

  &::after {
    content: "";
    display: block;
    padding-bottom: 100%;
  }
}

.section {
  margin: 1.5rem 0;

  &:first-child {
    margin-top: -0.5rem;
  }
}

.match-progress {
  width: 95%;
  height: 0.3125rem;
  margin-left: auto;
  margin-right: auto;
  background-color: var(--interaction-light);
  border-radius: 0.15625rem;
}

.match-progress div {
  height: 100%;
  border-radius: 0.15625rem;
}

.content-tab-body {
  margin: 0 1em 0 1em;
}

@media screen and (min-width: 768px) {
  .profile-picture,
  .smart-spider-box {
    width: 12.0625rem;
    height: 12.0625rem;
    flex: 0 0 12.0625rem;
  }

  .match-progress {
    width: 70%;
  }
}

.parliamentary-group {
  display: flex;
  justify-content: center;
}
</style>
